import { router, useFocusEffect, useGlobalSearchParams, useNavigation } from 'expo-router';
import { useCallback, useEffect } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { Divider } from 'react-native-paper';
import Animated, { useAnimatedRef } from 'react-native-reanimated';

import { useGetPrograms } from '@/api/hooks/programHooks';
import { Program } from '@/api/models/programModels';
import BrandedButton from '@/components/buttons/BrandedButton';
import DotsLoader from '@/components/DotsLoader';
import ListEmptyComponent from '@/components/ListEmptyComponent';
import { useScrollProps } from '@/components/navigation/TopTabsReanimated';
import ProgramCard from '@/components/program/ProgramCard';
import { useAuth } from '@/contexts/authContext';
import { i18n } from '@/utils/i18n';

const Programs = () => {
  const { state } = useAuth();
  const { npoId } = useGlobalSearchParams<{
    npoId: string;
    name: string;
  }>();

  const {
    data: programsResponse,
    refetch,
    isPending
  } = useGetPrograms({
    nonProfitOrgId: npoId ?? state.signUpData?.loginEmail?.npoId
  });

  const renderDivider = () => <Divider style={style.divider} />;

  // FocusEffect to refetch data once we come back from creating one program
  useFocusEffect(
    useCallback(() => {
      if (npoId) refetch();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [npoId])
  );

  //Locales
  const createProgram = i18n.t('profile.create_program');

  const { scrollEnabled, scrollTopHeader, scrollHandler } = useScrollProps();
  const navigation = useNavigation<any>();

  const programs = programsResponse?.data.flatMap((program) => program);

  const flatRef = useAnimatedRef<Animated.FlatList<any>>();

  useEffect(() => {
    const unsubscribe = navigation.addListener('tabPress', () => {
      scrollTopHeader();
      flatRef.current?.scrollToOffset({
        offset: 0,
        animated: false
      });
    });

    return unsubscribe;
  }, [flatRef, navigation, scrollTopHeader]);

  return (
    <View style={style.container}>
      {isPending ? (
        <DotsLoader />
      ) : (
        <Animated.FlatList
          ref={flatRef}
          onScroll={scrollHandler}
          scrollEnabled={scrollEnabled}
          showsVerticalScrollIndicator={false}
          bounces={false}
          style={style.fullWidth}
          data={programs}
          contentContainerStyle={style.listContentContainer}
          keyExtractor={(item: Program) => item.id}
          ItemSeparatorComponent={() => renderDivider()}
          ListHeaderComponentStyle={style.fullWidth}
          ListHeaderComponent={
            <BrandedButton
              style={style.createButton}
              onPress={() => {
                router.navigate('/create/program');
              }}>
              {createProgram}
            </BrandedButton>
          }
          renderItem={({ item }: { item: Program }) => (
            <ProgramCard
              canEdit={true}
              program={item}
              // onCardPress={() => {
              //   router.navigate({
              //     pathname: '/programDetail/[programId]',
              //     params: {
              //       programId: item.id,
              //       npoId: npoId ?? state.signUpData?.loginEmail?.npoId,
              //       npoName: npoName ?? '',
              //       programJson: JSON.stringify(item)
              //     }
              //   });
              // }}
              onDelete={refetch}
            />
          )}
          ListEmptyComponent={<ListEmptyComponent label={i18n.t('npo_tab.empty_programs')} />}
        />
      )}
    </View>
  );
};

export default Programs;

const style = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: Platform.OS !== 'web' ? 18 : 0
  },

  divider: {
    marginVertical: 25
  },

  listContentContainer: {
    gap: 25,
    paddingBottom: 35
  },

  createButton: {
    width: '100%',
    marginTop: 5
  },

  fullWidth: {
    width: '100%'
  }
});
